import $ from "jquery";
import Spinner from "../../spin.min";
import BoolTimer from "../../util/BoolTimer";
import messageResource from "../../messageResource";
import StringTokenizer from "../../util/StringTokenizer";
import makeRpcCall from "../../util/jquery_rpc";
import {KEY_CODE_ENTER, KEY_CODE_ESC, KEY_CODE_UP, KEY_CODE_DOWN} from "../../keyCodes";
import {arrayToSet} from "../../atUtils";

/**
 * @author kostenko
 *
 * A controller for task list and search field attached to it.
 *
 * Requires:
 *   jQuery
 *   jQuery.scrollintoview
 *   /js/util/jquery_rpc.js
 *   /js/constants_js.jsp
 *   /js/at.js
 *   /js/message_resources.js (using 'submit_tt' and 'js_escaped_macros.submit_tt' prefixes)
 *   /js/spin.min.js
 *
 * Note: the params below which accept DOM elements may also accept jQuery objects or selectors.
 *
 * @param container - task list container element, which contains all the needed markup
 *                    (for example of markup see 'taskList' element in task_search_control.jsp or task_select_control.jsp)
 * @param searchField - search text field element
 * @param initialRequestSpinnerPlaceholder - element in which to render spinner when doing an initial task list request
 *                                           (as opposed to the 'Show more' request)
 * @param isOwnTimeTrack - whether it is logged in user's own time-track page
 * @param isRecentTasksMode - in this mode:
 *                            - only those tasks are listed which have time tracked by the specified user,
 *                              ordered by last tracking date, most recent first
 *                            - tasks that are already added are not listed
 *                            - 'Show more tasks' link is not shown
 *                            - desiredPortionSize has no effect, only maxResultsPerPortion matters
 * @param isImportTasksMode - in this mode all tasks (open and completed) are shown
 * @param desiredPortionSize - desired number of tasks in a portion, must be > 0.
 * @param maxResultsPerPortion - maximum number of tasks in a portion, must be >= desiredPortionSize.
 * @param isMultiSelect - whether multiple selection is allowed (with checkboxes)
 * @param isEnableKeyboardNavigation - whether to move cursor with Up/Down keys and simulate click with Enter key
 * @param isHideOnEmptyQuery - whether the list should be hidden when no search query is entered
 * @param onRequestProcessingComplete - function executed when an RPC request is completed and its processing finished
 * @param onRequestError - function(jqXHR, textStatus, errorThrown) executed on RPC error.
 *                         Must return true if suppression of built-in error handler is needed.
 *                         See jquery_rpc.js for details.
 * @param onTaskSelect - executed when the user selects a task in single selection mode
 * @param onSelectionChange - executed after the set of selected tasks changes,
 *                            or after the list is cleared or re-built (regardless of the selection state before that).
 *                            Makes sense only in multi-selection mode.
 *                            Use getSelectedTaskIds method to obtain the actual list of selected tasks.
 * @constructor
 *
 * The following methods are declared:
 *   hide
 *   clearQuery
 *   makeInitialRequest
 *   getSelectedTaskIds
 *   clear
 * See their declaration in the constructor body for comments.
 *
 * The following entities are defined in at.js:
 * @name messageResource
 * @name messageResource.getMessageWithEscapedMacros
 * @name KEY_CODE_UP
 * @name KEY_CODE_DOWN
 * @name KEY_CODE_ESC
 * @name KEY_CODE_ENTER
 * @name BoolTimer
 * @name BoolTimer.switchValue
 * @name BoolTimer.isTrue
 */
class TaskListController {
    constructor({
                    container,
                    searchField,
                    initialRequestSpinnerPlaceholder,
                    isOwnTimeTrack = false,
                    isRecentTasksMode = false,
                    isImportTasksMode = false,
                    desiredPortionSize = 10,
                    maxResultsPerPortion = 20,
                    isMultiSelect = false,
                    isEnableKeyboardNavigation = false,
                    isHideOnEmptyQuery = false,
                    onRequestProcessingComplete,
                    onRequestError,
                    onTaskSelect,
                    onSelectionChange,
                    needClearButton = false,
                    disableAlreadyAdded = false,
                    findTasksService = null
                }) {
        // params
        this.isEnableKeyboardNavigation = isEnableKeyboardNavigation
        this.isMultiSelect = isMultiSelect
        this.onTaskSelect = onTaskSelect ? onTaskSelect : function() {}
        this.onRequestError = onRequestError ? onRequestError : function() {}
        this.onRequestProcessingComplete = onRequestProcessingComplete ? onRequestProcessingComplete : function() {}
        this.isImportTasksMode = isImportTasksMode
        this.isRecentTasksMode = isRecentTasksMode
        this.isOwnTimeTrack = isOwnTimeTrack
        this.onSelectionChange = onSelectionChange ? onSelectionChange : function() {}
        this.isHideOnEmptyQuery = isHideOnEmptyQuery
        this.desiredPortionSize = desiredPortionSize
        this.maxResultsPerPortion = maxResultsPerPortion
        this.disableAlreadyAdded = disableAlreadyAdded

        this.hoverEnabled = new BoolTimer(true, 100);
        this.spinner = new Spinner({length: 3, width: 2, radius: 3, lines: 8, color: '#aaaaaa', trail: 60});
        this.query = "";
        this.customerId = 0;
        this.projectId = 0;
        this.rpcRequest = null
        this.showMoreDisabled = false;
        this.isInActiveProjects = true;
        this.selectAllRows = false;
        this.findTaskService = findTasksService !== null
            ? findTasksService
            : (isImportTasksMode ? "TaskListControllerService.findTasksToImport" : "TaskListControllerService.findTasksToAdd");

        this.selectedUserId = -1
        this.selectedUserName = ''
        this.alreadyAddedTaskIdsSet = []

        this.container = $(container);
        this.searchField = $(searchField).searchField(this.processSearchQuery, $.trim, needClearButton);

        this.initialRequestSpinnerPlaceholder = $(initialRequestSpinnerPlaceholder);

        this.container.scroll(this.onScroll);
        this.container.mouseleave(this.onMouseLeave);
        this.searchField.keydown(this.onSearchFieldKeyDown);

        this.onInitialRequestComplete = this.onInitialRequestComplete.bind(this)

        this._taskList = []
    }


    /**
     * @param userId - id of the user whose time-track is being viewed/edited now
     * @param userName - display name of the selected user
     * @param addedTaskIds - array of ids of task which are already added to TT
     */
    setData(userId, userName, addedTaskIds) {
        this.selectedUserId = userId;
        this.selectedUserName = userName;
        this.alreadyAddedTaskIdsSet = arrayToSet(addedTaskIds);
    }

    /**
     * Hides the task list, aborting pending request.
     */
    hide() {
        this.abortRequestAndStopTimer();
        this.container.hide();
        this.query = '';   // clear remembered query, but not the text field,
        // to allow reloading results by pressing enter in the field
    }

    /**
     * Clears the search text box and does the necessary processing.
     */
    clearQuery(skipUpdate) {
        this.searchField.clearSearchField(skipUpdate);
        if (skipUpdate) {
            this.query = this.searchField.val();
        }
    }

    /**
     * Starts the initial RPC request (as opposed to 'show more' request) using the given customer or project id for
     * filtering. Note: if project id is given, then customer id is ignored by the server.
     * @param newCustomerId
     * @param newProjectId
     */
    makeInitialRequest(newCustomerId, newProjectId) {
        this.customerId = newCustomerId;
        this.projectId = newProjectId;
        this.selectAllRows = false;

        this.makeInitialRequest_internal();
    }

    /**
     * Returns the ids of the selected tasks (only makes sense in multi-select mode).
     * @return {Array}
     */
    getSelectedTaskIds() {
        const ids = [];

        this.container.children(".row.task").each((i, row) => {
            const id = this.getTaskId(row);
            if ($(row).find("input:checked").length > 0 && (!this.disableAlreadyAdded || !this.alreadyAddedTaskIdsSet[id]))
                ids.push(id);
        });

        return ids;
    }

    /**
     * Returns the ids of the visible tasks (only makes sense in multi-select mode).
     * @return {Array}
     */
    getVisibleTaskIds() {
        const ids = [];

        this.container.children(".row.task").each(function(i, row) {
            ids.push(this.getTaskId(row));
        });

        return ids;
    };

    /**
     * Clears the current results, aborting pending request.
     */
    clear() {
        this.abortRequestAndStopTimer();
        this.deleteAllRows();
        this.hideNoMatchesMessage();
    };

    isShown() {
        return this.container.is(":visible");
    };

    selectAll() {
        this.container.children(".row.task").each(function() {
            $(this).find("input").prop("checked", true).prop("disabled", true);
            $(this).addClass("selected");
        });
        this.selectAllRows = true;
        this.fireOnSelectionChange();
    };

    unselectAll() {
        this.container.children(".row.task").each(function() {
            $(this).find("input").prop("checked", false).prop("disabled", false);
            $(this).removeClass("selected");
        });
        this.selectAllRows = false;
        this.fireOnSelectionChange();
    };

    isSelectAllRows() {
        return this.selectAllRows;
    };

    getCustomerId() {
        return this.customerId;
    };

    getProjectId() {
        return this.projectId;
    };

    setInActiveProjects(inActive) {
        this.isInActiveProjects = inActive;
    };

    moveCursorUp() {
        this.moveCursorUpOrDown(true);
    }

    moveCursorDown() {
        this.moveCursorUpOrDown(false);
    }

    moveCursorUpOrDown(isUp) {
        let activeRow = this.getActiveRow();
        let switchTo = isUp ? this.getPrevRow(activeRow) : this.getNextRow(activeRow);

        if (switchTo.size() === 1) {
            this.setActiveRow(switchTo);
            this.hoverEnabled.switchValue(); // don't react to mouse move while changing active row the other way
            switchTo.scrollintoview({duration: 0});
        }
    }

    getActiveRow() {
        return this.container.children(".row.active");
    }

    setActiveRow(row) {
        this.getActiveRow().removeClass("active");
        $(row).addClass("active");
    }

    getNextRow(row) {
        return $(row).next(".row");
    }

    getPrevRow(row) {
        return $(row).prev(".row");
    }

    deleteAllRows() {
        this.container.children(".row").remove();
        this.fireOnSelectionChange();
    }

    getShownTasksCount() {
        return this.container.children(".row.task").length;
    }

    scrollToTop() {
        this.container.scrollTop(0);
    }

    isAlreadyAddedTask(task) {
        return !!this.alreadyAddedTaskIdsSet[task.id]
    }

    setRowContent(taskRow, task) {
        const alreadyAdded = this.isAlreadyAddedTask(task)

        taskRow.find(".taskName").html(this.getNamePresentationWithMatches(task.name, task.nameMatchMap.list));

        if (task.parentTaskName)
            taskRow.find(".taskName").prepend(
                this.getNamePresentationWithMatches(task.parentTaskName, task.parentTaskNameMatchMap.list),
                " / ");

        if (this.projectId === 0)
            taskRow.find(".details").text((this.customerId === 0 ? task.customerName + " / " : "") + task.projectName);
        else
            taskRow.find(".details").text("");

        if (alreadyAdded) {
            taskRow.find(".alreadyAdded").show();
            if (this.disableAlreadyAdded) {
                taskRow.find("input").prop("checked", true).prop("disabled", true);
            }
        }

        this.setAdditionalRowContent(taskRow, task)
    }

    setAdditionalRowContent(taskRow, task) {
        // abstract
    }

    /**
     * @param rpcResultData
     * @config tasks
     * @config resultsLeft
     */
    createRows(rpcResultData) {
        let shownTasksCount = this.getShownTasksCount();
        let firstNewRow = null;

        /*
         * @param this
         * @config id
         * @config name
         * @config nameMatchMap
         * @config parentTaskName
         * @config parentTaskNameMatchMap
         * @config projectName
         * @config customerName
         */
        $.each(rpcResultData.tasks.list, (i, task) => {
            // TODO move to function
            const alreadyAdded = this.isAlreadyAddedTask(task)

            if (this.isRecentTasksMode && alreadyAdded)
                return;

            let taskRow = this.container.find(".templates .row.task").clone().appendTo(this.container);
            taskRow.find(".taskId").text(task.id);

            this.setRowContent(taskRow, task)

            if (i === rpcResultData.tasks.list.length - 1 && rpcResultData.resultsLeft === 0)
                taskRow.addClass("last");

            taskRow.mousemove(() => this.onRowMouseMove(taskRow));
            taskRow.click(() => this.onRowClick(taskRow));

            if (this.isMultiSelect)
                taskRow.find("input").click(this.onRowCheckboxClick).change((e) => this.onRowCheckboxChange(taskRow.find("input")));

            shownTasksCount++;

            if (firstNewRow === null)
                firstNewRow = taskRow;

            if (this.selectAllRows) {
                taskRow.find("input").prop("checked", true).prop("disabled", true);
                taskRow.addClass("selected");
            }
        });

        this.container.children(".showMore").remove();

        if (!this.isRecentTasksMode && rpcResultData.resultsLeft > 0) {
            let showMoreRow = this.container.find(".templates .row.showMore").clone().appendTo(this.container);
            showMoreRow.find(".resultsContainDiv").html(
                messageResource.getMessageWithEscapedMacros("js_escaped_macros.submit_tt.task_search.results_contain_tasks",
                    [shownTasksCount, shownTasksCount + rpcResultData.resultsLeft]));

            showMoreRow.mousemove(() => this.onRowMouseMove(showMoreRow));
            showMoreRow.click(() => this.onRowClick(showMoreRow));
        }

        if (firstNewRow !== null) {
            if (this.isEnableKeyboardNavigation)
                this.setActiveRow(firstNewRow);

            this.container.animate({scrollTop: this.container.scrollTop() + firstNewRow.position().top});
        }

        this.showMoreDisabled = false;
    }

    spinInShowMoreRow() {
        const showMoreRow = this.container.children(".showMore");
        showMoreRow.find('.panelDiv').css('visibility', 'hidden');
        this.spinner.spin(showMoreRow[0]);
    }

    spinForInitialRequest() {
        this.spinner.spin(this.initialRequestSpinnerPlaceholder[0]);
    }

    stopSpin() {
        this.spinner.stop();
        this.container.children(".showMore").find(".panelDiv").css('visibility', 'visible'); // restore the 'show more' row
        // this will only have effect if 'show more' row was actually present
        // and this is needed only in case of 'show more' request error
        // (in case of success the 'show more' row will be replaced by more search results)
    }

    showNoMatchesMessage(text) {
        this.container.find(".noMatches").text(text).show();
    }

    hideNoMatchesMessage() {
        this.container.find(".noMatches").hide();
    }

    makeShowMoreRequest() {
        if (this.showMoreDisabled)
            return;

        this.showMoreDisabled = true; // disable 'show more' to prevent inconsistent results because of double-clicks

        this.rpcRequest = this._findTasksCall(
            {
                query: this.query,
                customerId: this.customerId,
                projectId: this.projectId,
                selectedUserId: this.selectedUserId,
                offset: this.getShownTasksCount(),
                desiredPortionSize: this.desiredPortionSize,
                maxResultsPerPortion: this.maxResultsPerPortion,
                isInActiveProjects: this.isInActiveProjects,
                isInArchivedProjects: !this.isInActiveProjects
            },
            this.onShowMoreRequestComplete,
            this.onRequestError_internal
        );

        this.spinInShowMoreRow();
    }

    getTaskId(row) {
        return Number($(row).find(".taskId").text());
    }

    abortRequestAndStopTimer() {
        clearTimeout(this.searchDelayTimer);

        if (this.rpcRequest)
            this.rpcRequest.abort();
    }

    prepareSearchQueryProcessing = (delay) => {
        this.abortRequestAndStopTimer();
        this.searchDelayTimer = setTimeout(() => {
            this.searchField.updateSearchField()
        }, delay);
    }

    makeInitialRequest_internal() {
        this.abortRequestAndStopTimer();

        this.showMoreDisabled = true; // disable 'show more' to prevent inconsistent results

        if (this.isRecentTasksMode)
            this.rpcRequest = this._findRecentTaskCall(
                [this.selectedUserId, this.maxResultsPerPortion],
                this.onInitialRequestComplete,
                this.onRequestError_internal
            )
        else
            this.rpcRequest = this._findTasksCall(
                {
                    query: this.query,
                    customerId: this.customerId,
                    projectId: this.projectId,
                    selectedUserId: this.selectedUserId,
                    offset: 0,
                    desiredPortionSize: this.desiredPortionSize,
                    maxResultsPerPortion: this.maxResultsPerPortion,
                    isInActiveProjects: this.isInActiveProjects,
                    isInArchivedProjects: !this.isInActiveProjects
                },
                this.onInitialRequestComplete,
                this.onRequestError_internal
            );
        this.spinForInitialRequest();
    }

    _findRecentTaskCall(params, onSuccess, onError) {
        return makeRpcCall("TaskListControllerService.findRecentTasksToAdd",
            params,
            onSuccess,
            onError
        );
    }

    _findTasksCall(params, onSuccess, onError) {
        return makeRpcCall(
            this.findTaskService,
            [
                params.query,
                params.customerId,
                params.projectId,
                params.selectedUserId,
                params.offset,
                params.desiredPortionSize,
                params.maxResultsPerPortion,
                params.isInActiveProjects,
                params.isInArchivedProjects
            ],
            onSuccess,
            onError
        )
    }

    processSearchQuery = (newQuery) => {
        this.query = newQuery;

        const tokenizer = new StringTokenizer(StringTokenizer.PUNCTUATORS_FOR_SEARCH);
        this.query = this.query.replace(tokenizer.replacePunctuatorsPattern, " ");


        if (this.query === '' && this.isHideOnEmptyQuery) {
            this.hide();
        }
        else {
            this.makeInitialRequest_internal();
        }
    }

    fireOnSelectionChange() {
        setTimeout(this.onSelectionChange, 0);
    }

    /**
     * TODO: this is a candidate for moving to some common file
     */
    getNamePresentationWithMatches(name, matchMap) {
        let result = $('<span/>');

        // by using 'text' method below we ensure that the strings will be properly escaped

        result.append($('<span/>').text(name.substr(0, matchMap[0])));

        for (let i = 0; i < matchMap.length; i += 2) {
            result.append($('<span class="highlightToken"/>').text(name.substring(matchMap[i], matchMap[i + 1])));
            result.append($('<span/>').text(name.substring(matchMap[i + 1], matchMap[i + 2])));
        }

        return result;
    }


    onRowMouseMove = (taskRow) => {
        if (this.hoverEnabled.isTrue())
            this.setActiveRow(taskRow);
    }

    onMouseLeave = () => {
        if (!this.isEnableKeyboardNavigation)
            this.setActiveRow(null);
    }

    onScroll = () => {
        this.hoverEnabled.switchValue(); // don't react to mouse move while scrolling
    }

    /**
     * @param data
     * @config assignmentsFeatureEnabled
     * @config viewerHasAccessToAllProjects
     */
    onInitialRequestComplete = (data) => {
        this.stopSpin();

        this.container.show();
        this.scrollToTop();
        this.deleteAllRows();
        this.hideNoMatchesMessage();

        this.createRows(data);

        if (this.getShownTasksCount() === 0) {
            let key = (this.isImportTasksMode ? 'task_selector' : 'submit_tt') + '.task_list.nothing_found';

            if (this.projectId !== 0)
                key += '.project_selected';
            else if (this.customerId !== 0)
                key += '.customer_selected';

            if (this.query !== '')
                key += '.name_given';

            if (this.isRecentTasksMode)
                key += '.recent';

            // TODO old texts need to change this
            if (this.projectId === 0 && !this.isRecentTasksMode) {
                if (!data.viewerHasAccessToAllProjects)
                    key += '.your';
                if (!this.isOwnTimeTrack && data.assignmentsFeatureEnabled)
                    key += '.his';
            }

            this.showNoMatchesMessage(messageResource.getMessage(key, [this.selectedUserName]));
        }

        this._taskList = data.tasks.list;
        this.onRequestProcessingComplete();
    }

    onShowMoreRequestComplete = (data) => {
        this.stopSpin();
        this.createRows(data);

        this._taskList = this._taskList.concat(data.tasks.list)
        this.onRequestProcessingComplete();
    }

    onRequestError_internal = (jqXHR, textStatus, errorThrown) => {
        this.stopSpin();
        this.showMoreDisabled = false;

        if (this.onRequestError(jqXHR, textStatus, errorThrown))
            return true;

        if (errorThrown.exception === 'com.actimind.actitime.services.NoRightsForRemoteCallException') {
            alert(messageResource.getMessage("common.no_rights_for_operation_anymore"));
            document.location.reload();
            return true;
        }

        if (errorThrown.exception === 'com.actimind.actitime.administration.NoSuchUserException') {
            document.location.reload();
            return true;
        }

        return false;
    }

    onRowClick = (taskRow) => {
        const taskId = this.getTaskId(taskRow);

        if (taskId > 0) {
            if (this.isMultiSelect) {
                $(taskRow).find("input").click();
            }
            else {
                this.abortRequestAndStopTimer();
                this.hide();
                this.onTaskSelect(taskId);
            }
        }
        else {
            this.makeShowMoreRequest();
        }
    }

    onRowCheckboxClick = (event) => {
        this.fireOnSelectionChange();
        event.stopPropagation(); // if event propagates to parent element (row) we'll stuck in a loop
    }

    onRowCheckboxChange = (input) => {
        if ($(input).prop('checked'))
            $(input).parents('.row.task').addClass('selected');
        else
            $(input).parents('.row.task').removeClass('selected');
    }

    onSearchFieldKeyDown = (event) => {
        if (event.keyCode === KEY_CODE_UP && this.isEnableKeyboardNavigation) {
            this.moveCursorUp();
            event.preventDefault();
        }
        else if (event.keyCode === KEY_CODE_DOWN && this.isEnableKeyboardNavigation) {
            this.moveCursorDown();
            event.preventDefault();
        }
        else if (event.keyCode === KEY_CODE_ENTER && this.isEnableKeyboardNavigation) {
            if (this.isShown())
                this.getActiveRow().click();
            else
                this.searchField.updateSearchField();

            event.preventDefault();
        }
        else if (event.keyCode === KEY_CODE_ESC) {
            this.clearQuery();
            event.preventDefault();
        }
        else {
            this.prepareSearchQueryProcessing(200);
        }
    }

    getLoadedTaskList() {
        return this._taskList;
    }
}

export default TaskListController;


// WEBPACK FOOTER //
// ./web/js/user/add_tasks/TaskListController.js